import React, {
  useRef, useState, useCallback, useEffect, useLayoutEffect,
} from 'react'
import { createPortal } from 'react-dom'
import { useCardFlyAnimation } from './hooks/useCardFlyAnimation'
import { getCardDimensions } from './hooks/getCardDimensions'
import { TarotPickACard } from './TarotPickACard'

export type TarotAnimationPhase = 'shuffling' | 'pickingCard' | 'jiggling' | 'transitioning' | 'landed' | 'idle'

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

interface TarotCardGreetingProps {
  backImageUrl?: string
  backImageReady?: boolean
  animationPhase: TarotAnimationPhase
  frontImageUrl?: string
  onTransitionEnd?: () => void
  onCardPicked?: () => void
  contentAreaRef?: React.RefObject<HTMLDivElement | null>
}

const DECK_CARD_INDICES = [0, 1, 2, 3, 4, 5, 6, 7] as const

export const TarotCardGreeting: React.FC<TarotCardGreetingProps> = ({
  backImageUrl,
  backImageReady = false,
  animationPhase,
  frontImageUrl,
  onTransitionEnd,
  onCardPicked,
  contentAreaRef,
}) => {
  const imageContainerRef = useRef<HTMLDivElement>(null)
  const flipperRef = useRef<HTMLDivElement>(null)
  const [capturedRect, setCapturedRect] = useState<Rect | null>(null)

  // Drive the fly animation when transitioning
  const handleFlyComplete = useCallback(() => {
    onTransitionEnd?.()
  }, [onTransitionEnd])

  useCardFlyAnimation({
    active: animationPhase === 'transitioning',
    moverRef: imageContainerRef,
    flipperRef,
    onComplete: handleFlyComplete,
  })

  // Capture a content-area-centered rect when animation starts.
  // Computed on pickingCard entry so TarotPickACard receives the jiggle target immediately,
  // then preserved for jiggling/transitioning. useLayoutEffect fires before paint.
  useLayoutEffect(() => {
    if (
      animationPhase !== 'pickingCard'
      && animationPhase !== 'jiggling'
      && animationPhase !== 'transitioning'
    ) return
    if (capturedRect) return
    const isMobile = window.innerWidth < 1025
    const area = contentAreaRef?.current?.getBoundingClientRect()
    const areaTop = area?.top ?? 0
    const areaLeft = area?.left ?? 0
    const areaWidth = area?.width ?? window.innerWidth
    const areaHeight = area?.height ?? window.innerHeight
    const cardDims = getCardDimensions(areaWidth, isMobile, areaHeight)
    const jiggleScale = isMobile ? 0.7 : 1
    const cardWidth = cardDims.width * jiggleScale
    const cardHeight = cardDims.height * jiggleScale
    setCapturedRect({
      top: areaTop + (areaHeight - cardHeight) / 2,
      left: areaLeft + (areaWidth - cardWidth) / 2,
      width: cardWidth,
      height: cardHeight,
    })
  }, [animationPhase, capturedRect, contentAreaRef])

  // Reset captured rect when phase returns to idle or shuffling
  useEffect(() => {
    if (animationPhase === 'idle' || animationPhase === 'shuffling') {
      setCapturedRect(null)
    }
  }, [animationPhase])

  // We intentionally render during 'landed' (but not 'idle'): the captured rect
  // and fly-animation inline styles persist on the DOM element, freezing the
  // flip card at the destination so it overlays the fading-in landed card and
  // prevents a brief flash at the handoff (AI-765).
  if (animationPhase === 'idle') {
    return null
  }

  const isShuffling = animationPhase === 'shuffling'
  const isPickingCard = animationPhase === 'pickingCard'
  const isJiggling = animationPhase === 'jiggling'
  const isLanded = animationPhase === 'landed'
  // Includes 'landed' — the card stays fixed at the destination as an overlay.
  const isAnimating = !isShuffling

  const flipperClass = [
    'tarot-card-greeting__flipper',
    isJiggling ? 'tarot-card-greeting__flipper--jiggling' : '',
  ].filter(Boolean).join(' ')

  // Fixed position while animating. During pickingCard we keep the container
  // mounted but hidden so the <img> is already decoded by the time TarotPickACard
  // unmounts — eliminates the empty-frame flash at the handoff. During 'landed'
  // the same fixed rect freezes the card at the destination as an overlay.
  const imageContainerStyle: React.CSSProperties | undefined = isAnimating && capturedRect
    ? {
      position: 'fixed',
      top: `${capturedRect.top}px`,
      left: `${capturedRect.left}px`,
      width: `${capturedRect.width}px`,
      height: `${capturedRect.height}px`,
      visibility: isPickingCard ? 'hidden' : 'visible',
    }
    : undefined

  const imageContainerClass = [
    'tarot-card-greeting__image-container',
    isAnimating ? 'tarot-card-greeting__image-container--fixed' : '',
    isLanded ? 'tarot-card-greeting__image-container--landed' : '',
  ].filter(Boolean).join(' ')

  return (
    <>
      <div className="tarot-card-greeting">
        {isShuffling && backImageUrl && (
          <div className="tarot-card-greeting__deck" aria-hidden="true">
            {DECK_CARD_INDICES.map((i) => (
              <div key={i} className={`tarot-card-greeting__deck-card tarot-card-greeting__deck-card--${i}`}>
                <img src={backImageUrl} alt="" draggable={false} />
              </div>
            ))}
          </div>
        )}
        {!isShuffling && backImageUrl && capturedRect && (
          <div
            className={imageContainerClass}
            ref={imageContainerRef}
            style={imageContainerStyle}
          >
            <div className={flipperClass} ref={flipperRef}>
              <img
                className="tarot-card-greeting__image"
                src={backImageUrl}
                alt=""
                decoding="sync"
              />
              {frontImageUrl && (
                <img
                  className="tarot-card-greeting__front-image"
                  src={frontImageUrl}
                  alt=""
                />
              )}
            </div>
          </div>
        )}
      </div>
      {isPickingCard && capturedRect && backImageUrl && backImageReady && createPortal(
        <TarotPickACard
          backImageUrl={backImageUrl}
          jiggleTarget={capturedRect}
          onComplete={onCardPicked ?? (() => {})}
        />,
        document.body,
      )}
    </>
  )
}
